class FeedbackForm {
  /**
   * A variable that may be assigned to a date in the future based on the user's behaviour,
   * and then saved to localStorage as the 'expiry' date after which the modal can be shown again.
   */
  static expires = null;

  /**
   * Returns whether or not the feedback form's saved/seen date has expired.
   *
   * @returns {boolean} True if feedback form's saved/seen date has expired.
   */
  static isExpired() {
    const today = new Date().toISOString();
    const expiry = localStorage.getItem('feedbackFormData') ? JSON.parse(localStorage.getItem('feedbackFormData')).expires : null;

    // Feedback form's visibility has an expiry date and it's in the future relative to `today`.
    if (expiry && expiry > today) {
      return false;
    }

    // Either localStorate doesn't have expiry date or it's in the past relative to `today`.
    return true;
  }

  /**
   * Validate the form based on filling required inputs.
   *
   * @param {object} formValues Object of key-value pairs of all feedback-form form fields.
   *
   * @returns {boolean} True if the required fields exist.
   */
  static checkValidation = (formValues) => {
    let isValid = false;

    if (formValues.whatToShare && formValues.focusOfComment) {
      isValid = true;
    }

    return isValid;
  };

  /**
   * Checks session storage for the amount of time remaining in the 'show modal' countdown; returns 60 if no storage value found.
   *
   * @returns {number} Number of seconds remaining in the 'show modal' countdown or 60 if no value found in session storage.
   */
  static timeRemaining() {
    return sessionStorage.getItem('feedbackFormTimeRemaining') ? Number(sessionStorage.getItem('feedbackFormTimeRemaining')) : 60;
  }

  /**
   * Sets an interval to countdown the timer, after which it will trigger the modal to show.
   *
   * @param {jQuery} $form jQuery object of the feedback-form form.
   *
   * @returns {null} Returns after running the timer sequence.
   */
  static runTimer = ($form) => {
    let timeRemaining = this.timeRemaining();

    const callback = () => {
      timeRemaining--;
      sessionStorage.setItem('feedbackFormTimeRemaining', timeRemaining);

      if (timeRemaining <= 0) {
        if (timeRemaining < 0) {
          sessionStorage.setItem('feedbackFormTimeRemaining', 0);
        }
        if (!Shopify.designMode) {
          $form.modal('show');
          // Analytics: feedback_form_start
          Shopify.analytics.publish('feedback_form_start');
        }
        clearInterval(timer);
        return;
      }
    }
    const timer = setInterval(callback, 1000);

    return;
  };

  /**
   * Prepares the form data object for local storage.
   *
   * @returns {object} The form data object to be stored in local storage.
   */
  static prepareFormDataForStorage() {
    const formData = {
      saved: new Date().toISOString(),
      expires: this.expires ?? null,
    };

    return formData;
  }

  /**
   * Removes personal information from user content.
   *
   * @param {string} string of content to be regEx'ed for email address and/or phone number.
   *
   * @returns {string|null} string with the sensitive data string-replaced, or null if no string passed.
   */
    static sanitizePersonalInformation(string = null) {
      if (!string) '';
      const emailRegex = /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/; /* eslint-disable-line */
      const phoneRegex = /(?:[-+() ]*\d){10,13}/g;
      string = string.replace(emailRegex, '[PII]');
      string = string.replace(phoneRegex, '[PII]');
      return string;
    }

  /**
   * Sets localStorage to hide the form for one month if it is closed.
   */
  static handleClose() {
    const now = new Date();
    now.setDate(now.getDate() + 30);
    this.expires = now.toISOString();

    this.storeFormData();
  }

  /**
   * Sets localStorage to hide the form for three months if it is submitted.
   *
   * @param {jQuery} $form jQuery object of the feedback-form form.
   */
  static handleSubmit($form) {
    const now = new Date();
    now.setDate(now.getDate() + 90);
    this.expires = now.toISOString();

    this.storeFormData();

    // Analytics: push data.
    const analyticsObj = {
      feedback_type: $form.find('input[name="what-to-share"]:checked').next('label').text().trim(), //value of "what do you want to share"
      feedback_focus: $form.find('input[name="focus-of-comment"]:checked').next('label').text().trim(), //value of "which describes the focus of your comment"
      feedback_text: this.sanitizePersonalInformation($form.find('#feedback-form-body').get(0).value)
    }

    Shopify.analytics.publish('feedback_form_complete', analyticsObj);
  }

  /**
   * Updates form UI based on current state of inputs.
   *
   * @param {jQuery} $form jQuery object of the feedback-form form.
   */
  static handleChange($form) {
    const {focusOfComment, whatToShare} = this.getFormValues($form);

    if (whatToShare) {
      const $whatToShare = $('input[name="what-to-share"]').first();
      $whatToShare.closest('fieldset').find('legend span').addClass('d-none');
    }

    if (focusOfComment) {
      const $focusOfComment = $('input[name="focus-of-comment"]').first();
      $focusOfComment.closest('fieldset').find('legend span').addClass('d-none');
    }
  }

  /**
   * Prepares the feedback form data object for local or session storage.
   */
  static storeFormData() {
    const formData = this.prepareFormDataForStorage();

    localStorage.setItem('feedbackFormData', JSON.stringify(formData));
  }

  /**
   * Creates an object of relevant values found in the from argument.
   *
   * @param {jQuery} $form jQuery object of the feedback-form form.
   *
   * @returns {object} Form values from the feedback-form form.
   */
  static getFormValues = ($form) => {
    const formObj = {
      focusOfComment: $form.find('input[name="focus-of-comment"]:checked').length ? $form.find('input[name="focus-of-comment"]:checked').val() : null,
      whatToShare: $form.find('input[name="what-to-share"]:checked').length ? $form.find('input[name="what-to-share"]:checked').val() : null,
    };

    return formObj;
  };
}

(($) => {
  $(() => {
    const isExpired = FeedbackForm.isExpired();
    if (isExpired) {
      const $feedbackFormModal = $('#feedback-form-modal');

      if ($feedbackFormModal.length) {
        const $feedbackFormForm = $feedbackFormModal.find('form');

        // UI functions.
        const showError = () => {
          const {focusOfComment, whatToShare} = FeedbackForm.getFormValues($feedbackFormForm);
          const $whatToShare = $('input[name="what-to-share"]').first();
          const $focusOfComment = $('input[name="focus-of-comment"]').first();

          let error;

          if (!whatToShare) {
            error = $whatToShare.closest('fieldset').find('legend span').removeClass('d-none').text();
          }

          if (!focusOfComment) {
            error = $focusOfComment.closest('fieldset').find('legend span').removeClass('d-none').text();
          }

          if (!whatToShare || !focusOfComment) {
            let $scrollToEl;
            if (!whatToShare) {
              $scrollToEl = $whatToShare;
            } else {
              $scrollToEl = $focusOfComment
            }
            $scrollToEl.closest('fieldset').get(0).scrollIntoView();
          }

          // Analytics: push error event.
          Shopify.analytics.publish('feedback_form_error', {
            feedback_error: error,
          });
        };

        const showSuccess = () => {
          $feedbackFormModal.find('.feedback-form__modal-header, .feedback-form__modal-body').hide().siblings('.feedback-form__success').show();
        };

        // Events.
        $feedbackFormModal.on('change', () => {
          FeedbackForm.handleChange($feedbackFormForm);
        });

        $feedbackFormModal.on('click', () => {
          FeedbackForm.handleClose($feedbackFormForm);
        });

        // Submit form if data is valid.
        $feedbackFormModal.on('submit', (evt) => {
          evt.preventDefault();
          let formValues = FeedbackForm.getFormValues($feedbackFormForm);
          if (FeedbackForm.checkValidation(formValues)) {
            FeedbackForm.handleSubmit($feedbackFormForm);
            showSuccess();
          } else {
            showError();
          }
        });

        // Start the countdown timer on load.
        FeedbackForm.runTimer($feedbackFormModal);
      }
    }
  });
})(jQuery);
